import type { ProjectDefinition } from "../../../types";
import {
  type FuncionProjectSpec,
  type GridSpec,
  conditions,
  excelDate,
  gridBodyRange,
  seededRandom,
} from "../spec";

const SHEET = "AG Ventas";
const TABLE = "VentasAG";

const REGIONES = ["Norte", "Sur", "Centro", "Occidente"] as const;
const VENDEDORES: Record<(typeof REGIONES)[number], string[]> = {
  Norte: ["Ana López", "Jorge Ramírez"],
  Sur: ["Luis Martínez", "Paola Jiménez"],
  Centro: ["Marta Sánchez", "Diego Castillo"],
  Occidente: ["Sofía Torres", "Ricardo Vargas"],
};
const CATEGORIAS = ["Laptops", "Monitores", "Accesorios", "Audio"] as const;
const PRODUCTOS: Record<(typeof CATEGORIAS)[number], [string, number][]> = {
  Laptops: [["Laptop Pro 14", 24999], ["Laptop Air 13", 18999], ["Laptop Gamer 16", 32999]],
  Monitores: [["Monitor 24 pulgadas", 3299], ["Monitor 27 pulgadas 4K", 7499], ["Monitor curvo 34", 9899]],
  Accesorios: [["Mouse inalámbrico", 349], ["Teclado mecánico", 1299], ["Hub USB-C", 799]],
  Audio: [["Audífonos con cancelación", 2899], ["Bocina Bluetooth", 899], ["Micrófono USB", 1899]],
};
const CANALES = ["Tienda", "En línea"] as const;

const rnd = seededRandom(3301);
const START = excelDate("2026-01-02");
// Fecha, Región, Vendedor, Categoría, Producto, Canal, Unidades, Importe
const ROWS = Array.from({ length: 64 }, () => {
  const region = rnd.pick(REGIONES);
  const categoria = rnd.pick(CATEGORIAS);
  const [producto, precio] = rnd.pick(PRODUCTOS[categoria]);
  const unidades = categoria === "Laptops" ? rnd.int(1, 4) : rnd.int(1, 12);
  return [
    START + rnd.int(0, 179),
    region,
    rnd.pick(VENDEDORES[region]),
    categoria,
    producto,
    rnd.pick(CANALES),
    unidades,
    unidades * precio,
  ] as [number, string, string, string, string, string, number, number];
}).sort((a, b) => a[0] - b[0]);

type Row = (typeof ROWS)[number];
const LAST = 4 + ROWS.length;
const total = (filter: (r: Row) => boolean) =>
  ROWS.filter(filter).reduce((sum, r) => sum + r[7], 0);

const IN = {
  region: "Norte",
  categoria: "Laptops",
  excluir: "Centro",
  desde: excelDate("2026-02-01"),
  hasta: excelDate("2026-04-30"),
};

const GRID: GridSpec = {
  anchor: "J21",
  corner: "Región \\ Categoría",
  rowHeaders: [...REGIONES],
  colHeaders: [...CATEGORIAS],
  format: "$#,##0",
};

export const spec: FuncionProjectSpec = {
  projectId: "funciones-sumarsiconjunto",
  sheets: [
    {
      name: SHEET,
      title: "Reporte de ventas",
      subtitle: "Ventas de enero a junio de 2026 — responde en las celdas amarillas",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Fecha", "Región", "Vendedor", "Categoría", "Producto", "Canal", "Unidades", "Importe"],
          rows: ROWS,
          formats: { Fecha: "dd/mm/yyyy", Importe: "$#,##0" },
        },
      ],
      grids: [GRID],
      inputs: [
        { address: "K4", label: "Región", value: IN.region },
        { address: "K5", label: "Categoría", value: IN.categoria },
        { address: "K6", label: "Región a excluir", value: IN.excluir },
        { address: "K7", label: "Desde", value: IN.desde, format: "dd/mm/yyyy" },
        { address: "K8", label: "Hasta", value: IN.hasta, format: "dd/mm/yyyy" },
      ],
      answers: [
        { address: "K11", label: "Tarea 1 · Total de la región", format: "$#,##0" },
        { address: "K12", label: "Tarea 2 · Región y categoría", format: "$#,##0" },
        { address: "K13", label: "Tarea 3 · Ventas grandes", format: "$#,##0" },
        { address: "K14", label: "Tarea 4 · Sin la región excluida", format: "$#,##0" },
        { address: "K15", label: "Tarea 5 · Monitores", format: "$#,##0" },
        { address: "K16", label: "Tarea 6 · Periodo en línea", format: "$#,##0" },
        { address: "K18", label: "Tarea 8 · Norte o Sur en tienda", format: "$#,##0" },
      ],
    },
  ],
  checks: {
    1: { kind: "cell", sheet: SHEET, address: "K11", fns: ["SUMIFS"], expected: total((r) => r[1] === IN.region) },
    2: {
      kind: "cell", sheet: SHEET, address: "K12", fns: ["SUMIFS"],
      expected: total((r) => r[1] === IN.region && r[3] === IN.categoria),
    },
    3: { kind: "cell", sheet: SHEET, address: "K13", fns: ["SUMIFS"], expected: total((r) => r[7] >= 20000) },
    4: { kind: "cell", sheet: SHEET, address: "K14", fns: ["SUMIFS"], expected: total((r) => r[1] !== IN.excluir) },
    5: {
      kind: "cell", sheet: SHEET, address: "K15", fns: ["SUMIFS"],
      expected: total((r) => r[4].toLowerCase().startsWith("monitor")),
    },
    6: {
      kind: "cell", sheet: SHEET, address: "K16", fns: ["SUMIFS"],
      expected: total((r) => r[0] >= IN.desde && r[0] <= IN.hasta && r[5] === "En línea"),
    },
    7: {
      kind: "block", sheet: SHEET, address: gridBodyRange(GRID), fns: ["SUMIFS"],
      expected: REGIONES.map((reg) => CATEGORIAS.map((cat) => total((r) => r[1] === reg && r[3] === cat))),
    },
    8: {
      kind: "cell", sheet: SHEET, address: "K18", fns: ["SUMIFS"], minCounts: { SUMIFS: 2 },
      expected: total((r) => (r[1] === "Norte" || r[1] === "Sur") && r[5] === "Tienda"),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;
const GRID_FORMULA = `=SUMAR.SI.CONJUNTO($H$5:$H$${LAST},$B$5:$B$${LAST},$J22,$D$5:$D$${LAST},K$21)`;

export const project: ProjectDefinition = {
  id: "funciones-sumarsiconjunto",
  title: "SUMAR.SI.CONJUNTO: Reporte de ventas",
  subtitle: "Sumar solo lo que cumple tus condiciones",
  description:
    "Eres analista comercial de una cadena de tecnología. Dirección quiere ver las ventas del semestre cortadas de muchas formas: por región, por categoría, por periodo, por canal…\n\nSUMAR.SI.CONJUNTO suma una columna pero solo en las filas que cumplen todas tus condiciones. Primero va el rango a sumar y después pares rango–criterio.",
  topics: [
    "Criterios de texto tomados de celdas",
    "Operadores: >=, <>, y fechas con &",
    "Comodines * en el criterio",
    "Tabla de doble entrada con referencias mixtas",
    "Lógica O: sumar dos SUMAR.SI.CONJUNTO",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Total de una región",
      description:
        "En la hoja <strong>AG Ventas</strong>, celda <strong>K11</strong>: suma el <strong>Importe</strong> de las ventas de la región escrita en <strong>K4</strong>. Usa la celda, no escribas \"Norte\" en la fórmula: así el reporte cambia cuando cambias K4.",
      solution: [
        `Selecciona ${F("K11")}.`,
        `El primer argumento es lo que quieres sumar: ${F("VentasAG[Importe]")}.`,
        `Después va un par rango–criterio: ${F("VentasAG[Región],K4")}.`,
        `Escribe ${F("=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Región],K4)")}. Cambia K4 a \"Sur\" para comprobar que se actualiza.`,
      ],
      expectedResult: "Total vendido en la región de K4",
      finalFormula: "=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Región],K4)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Región y categoría",
      description:
        "En <strong>K12</strong>: suma el Importe de la región de <strong>K4</strong> <em>y</em> la categoría de <strong>K5</strong>.",
      solution: [
        `Selecciona ${F("K12")}.`,
        "Cada condición extra es otro par rango–criterio. Todas tienen que cumplirse a la vez.",
        `Escribe ${F("=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Región],K4,VentasAG[Categoría],K5)")}.`,
      ],
      expectedResult: "Total de Laptops en la región Norte",
      finalFormula: "=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Región],K4,VentasAG[Categoría],K5)",
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Ventas grandes",
      description:
        "En <strong>K13</strong>: suma solo las ventas cuyo <strong>Importe</strong> sea de <strong>$20,000 o más</strong>.",
      solution: [
        `Selecciona ${F("K13")}.`,
        `Cuando el criterio lleva un operador, va entre comillas: ${F('">=20000"')}.`,
        "Aquí el rango a sumar y el rango del criterio son la misma columna, y eso está bien.",
        `Escribe ${F('=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Importe],">=20000")')}.`,
      ],
      expectedResult: "Suma de las ventas de $20,000 o más",
      finalFormula: '=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Importe],">=20000")',
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Todo menos una región",
      description:
        "En <strong>K14</strong>: suma el Importe de <strong>todas las regiones excepto</strong> la escrita en <strong>K6</strong>.",
      solution: [
        `Selecciona ${F("K14")}.`,
        `\"Distinto de\" es ${F('"<>"')}. Para unirlo con una celda usa ${F("&")}: ${F('"<>"&K6')}.`,
        `Escribe ${F('=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Región],"<>"&K6)')}.`,
      ],
      expectedResult: "Total sin la región Centro",
      finalFormula: '=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Región],"<>"&K6)',
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Todos los monitores",
      description:
        "En <strong>K15</strong>: suma el Importe de los productos cuyo nombre <strong>empieza con \"Monitor\"</strong>, sin importar el tamaño. Usa un <strong>comodín</strong>.",
      solution: [
        `Selecciona ${F("K15")}.`,
        `El asterisco ${F("*")} representa cualquier texto: ${F('"Monitor*"')} encuentra \"Monitor 24 pulgadas\", \"Monitor curvo 34\", etc.`,
        `Escribe ${F('=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Producto],"Monitor*")')}.`,
      ],
      expectedResult: "Total de los tres modelos de monitor",
      finalFormula: '=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Producto],"Monitor*")',
    },
    {
      id: 6,
      level: "intermedio",
      title: "Tarea 6: Ventas en línea de un periodo",
      description: conditions(
        "En <strong>K16</strong>: suma el Importe de las ventas que cumplan <strong>las tres</strong> condiciones:",
        [
          "Fecha <strong>desde</strong> la de <strong>K7</strong>",
          "Fecha <strong>hasta</strong> la de <strong>K8</strong>",
          "Canal <strong>En línea</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("K16")}.`,
        `Un rango de fechas son <strong>dos</strong> condiciones sobre la misma columna: ${F('VentasAG[Fecha],">="&K7')} y ${F('VentasAG[Fecha],"<="&K8')}.`,
        `Escribe ${F('=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Fecha],">="&K7,VentasAG[Fecha],"<="&K8,VentasAG[Canal],"En línea")')}.`,
      ],
      expectedResult: "Ventas en línea del 1/feb al 30/abr",
      finalFormula:
        '=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Fecha],">="&K7,VentasAG[Fecha],"<="&K8,VentasAG[Canal],"En línea")',
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Tabla Región × Categoría",
      description: conditions(
        "Llena la tabla de <strong>K22:N25</strong> con el total de cada región (filas) y categoría (columnas). Debe ser <strong>una sola fórmula</strong> escrita en K22 y arrastrada a toda la tabla:",
        [
          "La <strong>región</strong> sale de la columna <strong>J</strong> → fija la columna: <strong>$J22</strong>",
          "La <strong>categoría</strong> sale de la fila <strong>21</strong> → fija la fila: <strong>K$21</strong>",
          "Los rangos de datos no deben moverse → <strong>$H$5:$H$" + LAST + "</strong>, etc.",
        ]
      ),
      solution: [
        `Selecciona ${F("K22")}.`,
        "Aquí usamos rangos normales en vez de nombres de tabla: al arrastrar hacia la derecha, los nombres de columna de una tabla se recorren.",
        `Escribe ${F(GRID_FORMULA)}.`,
        `Arrastra el controlador de relleno hasta ${F("N22")} y luego hasta la fila ${F("25")}. Revisa que cada celda sume lo correcto.`,
      ],
      expectedResult: "16 totales, uno por región y categoría",
      finalFormula: GRID_FORMULA,
    },
    {
      id: 8,
      level: "reto",
      title: "Tarea 8: Norte o Sur, solo en tienda",
      description: conditions(
        "En <strong>K18</strong>: suma el Importe de las ventas en canal <strong>Tienda</strong> que sean de la región <strong>Norte</strong> <em>o</em> <strong>Sur</strong>.",
        [
          "SUMAR.SI.CONJUNTO solo combina condiciones con <strong>Y</strong>",
          "Para una <strong>O</strong>, suma dos SUMAR.SI.CONJUNTO: uno para Norte y otro para Sur",
        ]
      ),
      solution: [
        `Selecciona ${F("K18")}.`,
        "Si pusieras Región = Norte y Región = Sur en la misma fórmula, ninguna venta cumpliría las dos y el resultado sería 0.",
        `Escribe ${F('=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Región],"Norte",VentasAG[Canal],"Tienda")+SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Región],"Sur",VentasAG[Canal],"Tienda")')}.`,
      ],
      expectedResult: "Ventas en tienda de Norte más las de Sur",
      finalFormula:
        '=SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Región],"Norte",VentasAG[Canal],"Tienda")+SUMAR.SI.CONJUNTO(VentasAG[Importe],VentasAG[Región],"Sur",VentasAG[Canal],"Tienda")',
    },
  ],
};
